import { Capacitor } from '@capacitor/core';
import { useEffect, useState } from 'react';

/**
 * Height (px) of the on-screen keyboard band currently overlaying the layout
 * viewport.
 *
 * Why this exists (PD-14766): in the iOS WebView the Capacitor
 * `Keyboard.resize: 'body'` mode shrinks document.body when the keyboard
 * shows, but `position: fixed` overlays size to the LAYOUT viewport, which
 * does not shrink — so a fixed full-screen scroll container (the login
 * modal) keeps extending underneath the keyboard and its bottom content
 * cannot be scrolled into view. Consumers add this inset as bottom padding
 * so all content can scroll clear of the keyboard.
 *
 * The signal source is platform-specific:
 * - Native iOS: the Capacitor Keyboard plugin broadcasts `keyboardWillShow`
 *   / `keyboardWillHide` window events carrying the exact keyboard height.
 *   That is the authoritative signal there — WKWebView's visualViewport does
 *   NOT reflect the keyboard under the plugin's body-resize mode (verified
 *   on the iOS 26.5 simulator).
 * - Native Android: no listener at all. The webview itself resizes when the
 *   keyboard opens, so content is never obscured — padding on top of an
 *   already-resized viewport would double-compensate.
 * - Web (mobile Safari/Chrome): the keyboard overlays without resizing the
 *   layout viewport, and visualViewport shrinks to match — subtracting it
 *   from the layout viewport yields the obscured band.
 *
 * Returns 0 when no keyboard is showing or no signal is available.
 */
export function useKeyboardInset(): number {
	const [inset, setInset] = useState(0);

	useEffect(() => {
		if (Capacitor.isNativePlatform()) {
			if (Capacitor.getPlatform() !== 'ios') {
				return;
			}
			const onShow = (ev: Event): void => {
				const height = (ev as Event & { keyboardHeight?: number })
					.keyboardHeight;
				if (typeof height === 'number' && height > 0) {
					setInset(Math.round(height));
				}
			};
			const onHide = (): void => {
				setInset(0);
			};
			window.addEventListener('keyboardWillShow', onShow);
			window.addEventListener('keyboardDidShow', onShow);
			window.addEventListener('keyboardWillHide', onHide);
			return (): void => {
				window.removeEventListener('keyboardWillShow', onShow);
				window.removeEventListener('keyboardDidShow', onShow);
				window.removeEventListener('keyboardWillHide', onHide);
			};
		}

		const viewport = window.visualViewport;
		if (viewport == null) {
			return;
		}
		const update = (): void => {
			// offsetTop accounts for the visual viewport being panned (iOS
			// scrolls the page when focusing an input the keyboard would cover).
			const obscured =
				window.innerHeight - viewport.height - viewport.offsetTop;
			setInset(obscured > 0 ? Math.round(obscured) : 0);
		};
		update();
		viewport.addEventListener('resize', update);
		viewport.addEventListener('scroll', update);
		return (): void => {
			viewport.removeEventListener('resize', update);
			viewport.removeEventListener('scroll', update);
		};
	}, []);

	return inset;
}
